// ProjectClientEmail.jsx
// Independent project-specific client email component.
// Visual structure follows EmailTemplate.jsx:
// blurred modal, radial/linear surface, rounded-3xl workspaces,
// compact toggles, #365561 primary actions, and ReactQuill editor.

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext.jsx";
import { UserContext } from "../../contexts/UserContext.jsx";
import ProjectEmailComposer from "./ProjectEmailComposer";

const EMPTY_COMPOSER = {
  mode: "compose",
  mailboxEmail: "",
  messageID: "",
  toRecipients: "",
  ccRecipients: "",
  bccRecipients: "",
  subject: "",
  body: "",
  isReplyWithLink: false,
  linkTemplate: null,
};

function normaliseAddressList(recipients = []) {
  return (recipients || [])
    .map((item) => item?.emailAddress?.address)
    .filter(Boolean)
    .join(", ");
}

function senderInfo(message = {}) {
  return (
    message?.from?.emailAddress ||
    message?.sender?.emailAddress || {
      name: "",
      address: "",
    }
  );
}

function recipientAddresses(recipients = []) {
  return (recipients || [])
    .map((item) => item?.emailAddress?.address)
    .filter(Boolean);
}

function uniqueAddressList(addresses = []) {
  const seen = new Set();
  return addresses.filter((address) => {
    const key = String(address || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function formatMessageDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function stripHtml(value = "") {
  if (typeof document === "undefined") {
    return String(value).replace(/<[^>]+>/g, " ");
  }

  const node = document.createElement("div");
  node.innerHTML = value;
  return node.textContent || node.innerText || "";
}

function responseSubject(subject = "", prefix = "RE") {
  const value = String(subject || "").trim();

  // Always use the selected email's subject. Only normalise existing
  // response prefixes so we do not create "RE: FW: ..." or "FW: RE: ...".
  const baseSubject = value
    .replace(/^(?:(?:re|fw|fwd):\s*)+/i, "")
    .trim();

  return baseSubject ? `${prefix}: ${baseSubject}` : `${prefix}:`;
}

function replySubject(subject = "") {
  return responseSubject(subject, "RE");
}

function forwardSubject(subject = "") {
  return responseSubject(subject, "FW");
}

function getInitials(value = "") {
  const words = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "--";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
}

function Avatar({
  name = "",
  email = "",
  sizeClass = "h-9 w-9",
  textClass = "text-xs",
  tooltip = "",
  callingFrom = "",
}) {
  const source = name || email || "Unknown";
  const label = tooltip || [name, email].filter(Boolean).join(" · ");

  return (
    <span
      className={[
        "group relative inline-flex shrink-0 items-center justify-center rounded-2xl bg-[#365561] font-bold text-white shadow-sm",
        sizeClass,
        textClass,
      ].join(" ")}
      aria-label={label}
    >
      {getInitials(source)}

      {(label && callingFrom === "supporter") && (
        <span className="pointer-events-none absolute bottom-full left-1/2 z-[120] mb-2 hidden -translate-x-[20px] whitespace-nowrap rounded-xl bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-xl group-hover:block">
          {label}
          <span className="absolute left-5 top-full h-2 w-2 -translate-x-1/2 -translate-y-1 rotate-45 bg-slate-900" />
        </span>
      )}
    </span>
  );
}

const FOLDER_OPTIONS = [
  { value: "all", label: "All", fullLabel: "All mail", icon: "fa-inbox" },
  { value: "inbox", label: "Inbox", fullLabel: "Inbox", icon: "fa-envelope" },
  { value: "sent", label: "Sent", fullLabel: "Sent", icon: "fa-paper-plane" },
];

function FolderToggle({ value, onChange, counts = {} }) {
  return (
    <div className="relative inline-flex max-w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
      {FOLDER_OPTIONS.map((option) => {
        const isActive = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={[
              "relative z-10 inline-flex shrink-0 items-center gap-1 rounded-xl px-2 py-1.5 text-[10px] font-semibold transition-colors duration-200 focus:outline-none sm:gap-2 sm:px-3 sm:text-xs",
              isActive
                ? "text-white"
                : "text-slate-500 hover:bg-[#365561]/10 hover:text-[#365561]",
            ].join(" ")}
          >
            {isActive && (
              <motion.span
                layoutId="project-client-email-folder-toggle-pill"
                className="absolute inset-0 -z-10 rounded-xl bg-[#365561] shadow-[0_10px_24px_rgba(54,85,97,0.18)]"
                transition={{ type: "spring", stiffness: 420, damping: 32 }}
              />
            )}

            <i className={`fa-solid ${option.icon} text-[10px] sm:text-xs`} />
            <span>{option.label}</span>
            <span
              className={[
                "rounded-full px-1.5 py-0.5 text-[9px] leading-none",
                isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500",
              ].join(" ")}
            >
              {counts[option.value] || 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function NavButton({
  active,
  icon,
  label,
  count,
  onClick,
  unread = false,
  compact = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={[
        "group relative flex cursor-pointer items-center text-left transition focus:outline-none",
        compact ? "w-full justify-center gap-0 px-2 py-2" : "w-full gap-3 px-3 py-2.5",
        active
          ? "bg-[#365561] text-white shadow-[0_10px_24px_rgba(54,85,97,0.20)]"
          : "text-slate-600 hover:bg-[#365561]/10 hover:text-[#365561]",
      ].join(" ")}
    >
      <span
        className={[
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
          active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-500",
        ].join(" ")}
      >
        <i className={`fa-solid ${icon}`} />
      </span>

      {!compact && (
				<>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{label}</span>
				<span
					className={[
						"rounded-full px-2 py-0.5 text-[10px] font-bold",
						active
							? "bg-white/20 text-white"
							: unread
								? "bg-rose-50 text-rose-600"
								: "bg-slate-100 text-slate-500",
					].join(" ")}
				>
					{count || 0}
				</span>
				</>
      )}

      

      {compact && (
				<>
					<span
						className={[
							"absolute top-1 right-2 text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold",
							active
								? "bg-white/20 text-white"
								: unread
									? "bg-rose-50 text-rose-600"
									: "bg-rose-100 text-rose-500",
						].join(" ")}
					>
						{count || 0}
					</span>
					<span className="pointer-events-none absolute left-full top-1/2 z-[120] ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-xl bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-xl group-hover:block">
						{label}
						<span className="absolute right-full top-1/2 h-2 w-2 -translate-y-1/2 translate-x-1 rotate-45 bg-slate-900" />
					</span>
				</>
      )}
    </button>
  );
}

function TooltipIconButton({
  icon,
  label,
  onClick,
  primary = false,
  disabled = false,
  isLeftEdge = false,
  isRightEdge = false,
  tooltipPosition = "bottom",
}) {
  const verticalClass =
    tooltipPosition === "top"
      ? "bottom-full mb-2"
      : "top-full mt-2";

  const horizontalClass = isLeftEdge
    ? "left-0"
    : isRightEdge
      ? "right-0"
      : "left-1/2 -translate-x-1/2";

  const arrowVerticalClass =
    tooltipPosition === "top"
      ? "top-full -translate-y-1"
      : "bottom-full translate-y-1";

  const arrowHorizontalClass = isLeftEdge
    ? "left-3"
    : isRightEdge
      ? "right-3"
      : "left-1/2 -translate-x-1/2";

  return (
    <button
      type="button"
      onClick={onClick}
      // disabled={disabled}
      aria-label={label}
      className={[
        "group relative inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center overflow-visible rounded-full transition focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:h-8 sm:w-8",
        primary
          ? "bg-[#365561] text-white shadow-[0_14px_30px_rgba(54,85,97,0.24)] hover:bg-[#2c4650]"
          : "border border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#365561]/25 hover:bg-[#365561]/10 hover:text-[#365561]",
      ].join(" ")}
    >
      <i className={`fa-solid ${icon} text-xs`} />

      <span
        className={[
          "pointer-events-none absolute z-[100] hidden whitespace-nowrap rounded-xl bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-xl group-hover:block",
          verticalClass,
          horizontalClass,
        ].join(" ")}
      >
        {label}

        <span
          className={[
            "absolute h-2 w-2 rotate-45 bg-slate-900",
            arrowVerticalClass,
            arrowHorizontalClass,
          ].join(" ")}
        />
      </span>
    </button>
  );
}

function ReplyWithLinkMenu({ templates = [], onSelect, disabled = false }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const hasTemplates = templates.length > 0;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        // disabled={!hasTemplates || disabled}
        aria-label="Reply with link"
        aria-haspopup="menu"
        aria-expanded={open}
        className="group relative inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center overflow-visible rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-[#365561]/25 hover:bg-[#365561]/10 hover:text-[#365561] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:h-8 sm:w-8"
      >
        {/* <i className="fa-solid fa-share-nodes text-xs" /> */}
        <i  className="fa-solid fa-reply test-xs" />

        {!open && (
          <span className="pointer-events-none absolute right-0 top-full z-[100] mt-2 hidden whitespace-nowrap rounded-xl bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-xl group-hover:block">
            Reply with link
            <span className="absolute bottom-full right-3 h-2 w-2 translate-y-1 rotate-45 bg-slate-900" />
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-[160] mt-2 w-[280px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-[0_18px_50px_rgba(15,23,42,0.20)]"
        >
          <div className="px-2.5 pb-2 pt-1.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#365561]">
              Reply with link
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">
              Choose an email template
            </p>
          </div>

          <div className="grid gap-1 pr-2">
            {templates.map((template, index) => (
              <button
                key={`${template.name || template.label || "template"}-${index}`}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onSelect?.(template);
                }}
                className="group flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition hover:bg-[#365561]/10 focus:bg-[#365561]/10 focus:outline-none"
              >
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#365561]/10 text-[10px] font-bold uppercase text-[#365561] transition group-hover:bg-[#365561] group-hover:text-white">
                  {template.lang || "--"}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-semibold text-slate-700">
                    {template.name || template.label || `Template ${index + 1}`}
                  </span>
                  <span className="block truncate text-[10px] text-slate-400">
                    {template.label || "Template"}
                  </span>
                </span>

                <i className="fa-solid fa-chevron-right text-[9px] text-slate-300 transition group-hover:text-[#365561]" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmailIconButton({
  icon,
  label,
  onClick,
  primary = false,
  disabled = false,
  isLeftEdge = false,
  isRightEdge = false,
  tooltipPosition = "bottom",
}) {
  const verticalClass =
    tooltipPosition === "top"
      ? "bottom-full mb-2"
      : "top-full mt-2";

  const horizontalClass = isLeftEdge
    ? "left-0"
    : isRightEdge
      ? "right-0"
      : "left-1/2 -translate-x-1/2";

  const arrowVerticalClass =
    tooltipPosition === "top"
      ? "top-full -translate-y-1"
      : "bottom-full translate-y-1";

  const arrowHorizontalClass = isLeftEdge
    ? "left-3"
    : isRightEdge
      ? "right-3"
      : "left-1/2 -translate-x-1/2";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={[
        "group relative flex cursor-pointer items-center text-left transition focus:outline-none",
        "w-full justify-center gap-0 px-2 py-2",
      ].join(" ")}
    >
      <span
        className={[
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
          "bg-slate-100 text-slate-500",
        ].join(" ")}
      >
        <i className={`fa-solid ${icon}`} />
      </span>      

      <span
				className={[
					"absolute top-1 right-0 text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold",
					"text-sky-500",
				].join(" ")}
			>
				{'New'}
			</span>

			<span className="pointer-events-none absolute left-full top-1/2 z-[120] ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-xl bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-xl group-hover:block">
				{label}
				<span className="absolute right-full top-1/2 h-2 w-2 -translate-y-1/2 translate-x-1 rotate-45 bg-slate-900" />
			</span>
				
    </button>
  );
}

function MailboxBadge({ message }) {
  return (
    <span className="rounded-full bg-[#365561]/10 px-2 py-0.5 text-[10px] font-bold text-[#365561]">
      {message.mailboxRole === "owner" ? "Owner" : "Supporter"} ·{" "}
      {message.mailboxName || message.mailboxEmail}
    </span>
  );
}

function MessageListItem({ message, selected, onClick }) {
  const sender = senderInfo(message);
  const isInbox = message.folder === "inbox";
  const title = isInbox
    ? sender.name || sender.address || "Unknown sender"
    : normaliseAddressList(message.toRecipients) || "Unknown recipient";

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "w-full cursor-pointer rounded-2xl border p-3 text-left transition focus:outline-none",
        selected
          ? "border-[#365561]/35 bg-[#365561]/10 shadow-[0_12px_30px_rgba(54,85,97,0.10)]"
          : "border-slate-200 bg-white hover:border-[#365561]/25 hover:bg-slate-50",
        isInbox && !message.isRead ? "ring-1 ring-blue-100" : "",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isInbox ? (
            <Avatar
              name={sender.name}
              email={sender.address}
              sizeClass="h-8 w-8"
              textClass="text-[10px]"
            />
          ) : (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
              <i className="fa-solid fa-paper-plane text-xs" />
            </span>
          )}

          <div className="min-w-0 flex-1">
            <p
              className={[
                "truncate text-sm text-slate-900",
                isInbox && !message.isRead ? "font-bold" : "font-semibold",
              ].join(" ")}
            >
              {isInbox ? title : `To: ${title}`}
            </p>
            <p className="truncate text-[10px] text-slate-400">
              {message.mailboxName || message.mailboxEmail}
            </p>
          </div>
        </div>

        <span className="shrink-0 text-[10px] font-medium text-slate-400">
          {formatMessageDate(message.sortDate)}
        </span>
      </div>

      <p className="mt-2 truncate text-xs font-semibold text-slate-700">
        {message.subject || "(No subject)"}
      </p>

      <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
        {message.bodyPreview || stripHtml(message.body?.content || "")}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span
          className={[
            "rounded-full px-2 py-0.5 text-[10px] font-bold",
            isInbox ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700",
          ].join(" ")}
        >
          {isInbox ? "Inbox" : "Sent"}
        </span>

        {message.hasAttachments && (
          <span className="text-[10px] text-slate-400">
            <i className="fa-solid fa-paperclip" /> Attachment
          </span>
        )}
      </div>
    </button>
  );
}

function EmailBody({ htmlBody = "" }) {
  const bodyRef = useRef(null);

  const cleanBody = useMemo(
    () =>
      DOMPurify.sanitize(htmlBody, {
        USE_PROFILES: { html: true },
      }),
    [htmlBody]
  );

  useEffect(() => {
    if (!bodyRef.current) return;

    const images = Array.from(bodyRef.current.querySelectorAll("img"));

    images.forEach((image) => {
      image.style.width = "auto";
      image.style.height = "auto";
      image.style.maxWidth = "100%";
      image.style.objectFit = "contain";
    });

    const lastThreeImages = images.slice(-3);
    lastThreeImages.forEach((image, index) => {
      image.style.width = "auto";
      image.style.height = "auto";
      image.style.maxWidth = index === 1 ? "95px" : "100%";
      image.style.maxHeight = index === 1 ? "150px" : "1000px";

      if (index === 1) {
        image.style.marginLeft = "2px";
      }
    });
  }, [cleanBody]);

  return (
    <div
      ref={bodyRef}
      className="project-client-email-html text-sm leading-6 text-slate-700"
      dangerouslySetInnerHTML={{ __html: cleanBody }}
    />
  );
}


function formatAttachmentSize(size = 0) {
  const bytes = Number(size) || 0;

  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentIcon(contentType = "", name = "") {
  const type = String(contentType || "").toLowerCase();
  const filename = String(name || "").toLowerCase();

  if (type.includes("image") || /\.(png|jpe?g|gif|webp|svg)$/i.test(filename)) {
    return "fa-file-image";
  }

  if (type.includes("pdf") || filename.endsWith(".pdf")) {
    return "fa-file-pdf";
  }

  if (
    type.includes("word") ||
    /\.(doc|docx)$/i.test(filename)
  ) {
    return "fa-file-word";
  }

  if (
    type.includes("excel") ||
    type.includes("spreadsheet") ||
    /\.(xls|xlsx|csv)$/i.test(filename)
  ) {
    return "fa-file-excel";
  }

  if (
    type.includes("presentation") ||
    type.includes("powerpoint") ||
    /\.(ppt|pptx)$/i.test(filename)
  ) {
    return "fa-file-powerpoint";
  }

  if (
    type.includes("zip") ||
    type.includes("compressed") ||
    /\.(zip|rar|7z)$/i.test(filename)
  ) {
    return "fa-file-zipper";
  }

  return "fa-file";
}

function downloadGraphAttachment(attachment = {}) {
  const base64 = String(attachment.base64 || "").trim();

  if (!base64) {
    Swal.fire({
      icon: "warning",
      title: "Attachment unavailable",
      text: "Microsoft Graph did not return downloadable file content for this attachment.",
    });
    return;
  }

  try {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    const blob = new Blob([bytes], {
      type: attachment.contentType || "application/octet-stream",
    });

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = attachment.name || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch (error) {
    console.error("Could not download attachment:", error);

    Swal.fire({
      icon: "error",
      title: "Could not download attachment",
      text: "The attachment data could not be converted into a file.",
    });
  }
}

function MessageAttachments({ attachments = [] }) {
  const visibleAttachments = (attachments || []).filter(
    (attachment) => !attachment?.isInline
  );

  if (!visibleAttachments.length) return null;

  return (
    <div className="shrink-0 border-b border-slate-200 bg-slate-50/80 px-4 py-3 sm:px-5">
      <div className="mb-2 flex items-center gap-2">
        <i className="fa-solid fa-paperclip text-xs text-[#365561]" />
        <p className="text-xs font-semibold text-slate-700">
          {visibleAttachments.length} attachment
          {visibleAttachments.length === 1 ? "" : "s"}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {visibleAttachments.map((attachment, index) => (
          <button
            key={attachment.id || `${attachment.name}-${index}`}
            type="button"
            onClick={() => downloadGraphAttachment(attachment)}
            className="group flex min-w-0 max-w-full cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left shadow-sm transition hover:border-[#365561]/30 hover:bg-[#365561]/5 sm:max-w-[300px]"
            title={`Download ${attachment.name || "attachment"}`}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition group-hover:bg-[#365561]/10 group-hover:text-[#365561]">
              <i
                className={`fa-solid ${attachmentIcon(
                  attachment.contentType,
                  attachment.name
                )}`}
              />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-semibold text-slate-800">
                {attachment.name || "Attachment"}
              </span>
              <span className="block truncate text-[11px] text-slate-500">
                {formatAttachmentSize(attachment.size)}
              </span>
            </span>

            <i className="fa-solid fa-download shrink-0 text-xs text-slate-300 transition group-hover:text-[#365561]" />
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageDetail({
  isLink = false,
  message,
  onReply,
  onReplyAll,
  onForward,
  onReplyWithLink,
  replyWithLinkTemplates = [],
  canRespond = false,
  onBack,
  showBackButton = false,
}) {
  if (!message) {
    return (
      <div className="flex h-full min-h-[420px] items-center justify-center px-5 text-center">
        <div>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-[#365561]/10 text-[#365561]">
            <i className="fa-regular fa-envelope-open text-2xl" />
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-700">Select an email</p>
          <p className="mt-1 text-sm text-slate-500">
            Choose a client message from the list to read or respond.
          </p>
        </div>
      </div>
    );
  }

  const sender = senderInfo(message);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-slate-200 px-4 py-3">
        <div className="flex items-start gap-3">
          {showBackButton && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-[#365561] md:hidden"
              aria-label="Back to messages"
            >
              <i className="fa-solid fa-arrow-left text-xs" />
            </button>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <h3 className="min-w-0 flex-1 text-lg font-semibold text-slate-900">
                {message.subject || "(No subject)"}
              </h3>

              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {/* <TooltipIconButton
                  icon="fa-reply"
                  label="Reply"
                  onClick={onReply}
                  primary={canRespond}
                  disabled={!canRespond}
                />
                <TooltipIconButton
                  icon="fa-reply-all"
                  label="Reply all"
                  onClick={onReplyAll}
                  disabled={!canRespond}
                />
                <TooltipIconButton
                  icon="fa-share"
                  label="Forward"
                  onClick={onForward}
                /> */}
                {isLink && (
                  <ReplyWithLinkMenu
                    templates={replyWithLinkTemplates}
                    onSelect={onReplyWithLink}
                    disabled={!canRespond}
                  />
                )}
              </div>
            </div>

            <div className="mt-4 flex items-start gap-3">
              <Avatar
                name={sender.name}
                email={sender.address}
                sizeClass="h-10 w-10"
                textClass="text-xs"
              />

              <div className="min-w-0 flex-1 text-xs text-slate-500">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-700">
                      {sender.name || sender.address || "Unknown sender"}
                    </p>
                    <p className="truncate text-[11px] text-slate-400">
                      {sender.address || "unknown"}
                    </p>
                  </div>

                  <span className="shrink-0 whitespace-nowrap text-[10px] text-slate-400">
                    {formatMessageDate(message.sortDate)}
                  </span>
                </div>

                <div className="mt-2 grid gap-1">
                  <p className="flex min-w-0 gap-2">
                    <span className="w-5 shrink-0 font-semibold text-slate-700">To:</span>
                    <span className="min-w-0 flex-1 break-words">
                      {normaliseAddressList(message.toRecipients) || "—"}
                    </span>
                  </p>

                  {message.ccRecipients?.length > 0 && (
                    <p className="flex min-w-0 gap-2">
                      <span className="w-5 shrink-0 font-semibold text-slate-700">Cc:</span>
                      <span className="min-w-0 flex-1 break-words">
                        {normaliseAddressList(message.ccRecipients)}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MessageAttachments attachments={message.attachments || []} />

      <div className="min-h-0 flex-1 overflow-y-auto bg-white px-5 py-5">
        <EmailBody htmlBody={message.body?.content || ""} />
      </div>
    </div>
  );
}

export default function ProjectClientEmail({
  projectID,
  expertID = null,
  projectName = "",
  isLinkCreated,
  onClose,
  logFunction,
}) {
  const { API_URL } = useContext(UrlContext);
  const { userData } = useContext(UserContext);

  const getCurrentUserGraphToken = () => {
    return localStorage.getItem("accesstoken") || "";
  };

  const [activeFolder, setActiveFolder] = useState("all");
  const [messages, setMessages] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [clientEmail, setClientEmail] = useState("");
  const [counts, setCounts] = useState({
    all: 0,
    inbox: 0,
    sent: 0,
    unread: 0,
  });
  const [selectedMessageID, setSelectedMessageID] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [composer, setComposer] = useState(null);
  const [showResponsiveDetail, setShowResponsiveDetail] = useState(false);
  const [defaultTemplate, setDefaultTemplate] = useState([]);
  const [clientName, setClientName] = useState({});
  const [linkData, setLinkData] = useState({});

  // Titles that should be removed
  const TITLES = [
    "Dr.", "Dr", 
    "Mr.", "Mr", 
    "Mrs.", "Mrs",
    "Ms.", "Ms",
    "Prof.", "Prof"
  ];

  function cleanName(fullName = "") {
    if (!fullName) return "";

    let name = fullName.trim();

    TITLES.forEach(title => {
      const regex = new RegExp("^" + title.replace(".", "\\.?") + "\\s+", "i");
      name = name.replace(regex, "");
    });

    return name.trim();
  };

  // Formal → always return LAST NAME
  function getFormalName(fullName = "") {
    const name = cleanName(fullName);
    const parts = name.split(" ").filter(Boolean);

    if (parts.length === 0) return "";
    return parts[parts.length - 1]; // last word = last name
  };

  // Informal → always return FIRST NAME
  function getInformalName(fullName = "") {
    const name = cleanName(fullName);
    const parts = name.split(" ").filter(Boolean);

    if (parts.length === 0) return "";

    // Ignore initials like "T" or "M"
    const firstRealName = parts.find(part => part.length > 1);
    return firstRealName || parts[0];
  };

  const selectedMessage = useMemo(
    () => messages.find((item) => item.id === selectedMessageID) || null,
    [messages, selectedMessageID]
  );

  const canRespondToSelectedMessage = useMemo(() => {
    const selectedMailboxEmail = String(
      selectedMessage?.mailboxEmail || ""
    ).trim().toLowerCase();

    const currentUserEmail = String(
      userData?.email || ""
    ).trim().toLowerCase();

    return Boolean(
      selectedMailboxEmail &&
      currentUserEmail &&
      selectedMailboxEmail === currentUserEmail
    );
  }, [selectedMessage, userData?.email]);

  const visibleMessages = useMemo(() => {
    if (activeFolder === "all") return messages;
    return messages.filter((message) => message.folder === activeFolder);
  }, [activeFolder, messages]);

  const changeFolder = (folder) => {
    setActiveFolder(folder);
    setShowResponsiveDetail(false);
  };

  const selectMessage = (message) => {
    setSelectedMessageID(message.id);
    setShowResponsiveDetail(true);
  };

  const closeResponsiveDetail = () => {
    setShowResponsiveDetail(false);
  };

  const postJSON = async (endpoint, payload) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json().catch(() => ({}));

    if (!response.ok || responseData.status !== "success") {
      throw new Error(
        responseData.text || `Request failed with HTTP ${response.status}.`
      );
    }

    return responseData;
  };

  const loadEmails = async ({ preserveSelection = true } = {}) => {
    setLoading(true);

    try {
      const responseData = await postJSON("get-project-client-emails", {
        projectID,
        expertID,
      });

      const nextMessages = responseData.emailData || [];
      setMessages(nextMessages);
      setMailboxes(responseData.mailboxes || []);
      setClientEmail(responseData.clientEmail || "");
      setCounts(
        responseData.counts || {
          all: nextMessages.length,
          inbox: 0,
          sent: 0,
          unread: 0,
        }
      );

      setSelectedMessageID((current) => {
        if (
          preserveSelection &&
          current &&
          nextMessages.some((message) => message.id === current)
        ) {
          return current;
        }
        return nextMessages[0]?.id || "";
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not load project emails",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectID) loadEmails({ preserveSelection: false });
  }, [projectID, expertID]);

  const openCompose = () => {
    const firstMailbox = mailboxes[0]?.email || "";
    setShowResponsiveDetail(true);
    setComposer({
      ...EMPTY_COMPOSER,
      mode: "compose",
      mailboxEmail: firstMailbox,
      toRecipients: clientEmail ? [clientEmail] : [],
      ccRecipients: [],
      bccRecipients: [],
      subject: `expertpowerhouse | ${projectName || `Project ${projectID}`}`,
      body: "",
      originalMessage: null,
    });
  };

  const openResponse = (mode, linkTemplate = null) => {
    if (!selectedMessage) return;

    const isReplyWithLink = mode === "link";
    const isReplyAction =
      mode === "reply" ||
      mode === "replyAll" ||
      isReplyWithLink;

    // if (isReplyAction && !canRespondToSelectedMessage) return;
    const senderAddress = senderInfo(selectedMessage)?.address || "";
    const selectedMailbox = String(
      selectedMessage.mailboxEmail || ""
    ).trim().toLowerCase();
    const currentUserEmail = String(
      userData?.email || ""
    ).trim().toLowerCase();

    const originalTo = recipientAddresses(selectedMessage.toRecipients);
    const originalCc = recipientAddresses(selectedMessage.ccRecipients);

    let toRecipients = [];
    let ccRecipients = [];

    const isSentMessage = selectedMessage.folder === "sent";
    const mailboxBelongsToCurrentUser = Boolean(
      selectedMailbox &&
      currentUserEmail &&
      selectedMailbox === currentUserEmail
    );

    // Inbox behaviour stays unchanged.
    // For Sent messages, Reply / Reply All / Reply with link all target the
    // original To recipients and never put the mailbox owner in To.
    if (mode === "reply") {
      toRecipients = isSentMessage
        ? uniqueAddressList(
            originalTo.filter(
              (address) =>
                String(address || "").trim().toLowerCase() !== selectedMailbox
            )
          )
        : senderAddress
          ? [senderAddress]
          : [];
    } else if (mode === "replyAll" || isReplyWithLink) {
      toRecipients = isSentMessage
        ? uniqueAddressList(
            originalTo.filter(
              (address) =>
                String(address || "").trim().toLowerCase() !== selectedMailbox
            )
          )
        : uniqueAddressList([
            senderAddress,
            ...originalTo.filter(
              (address) =>
                String(address || "").trim().toLowerCase() !== selectedMailbox
            ),
          ]);

      ccRecipients = uniqueAddressList(
        originalCc.filter(
          (address) =>
            String(address || "").trim().toLowerCase() !== selectedMailbox &&
            !toRecipients.some(
              (toAddress) =>
                String(toAddress || "").trim().toLowerCase() ===
                String(address || "").trim().toLowerCase()
            )
        )
      );
    }

    if (isReplyWithLink) {
      const toRecipientSet = new Set(
        toRecipients.map((address) =>
          String(address || "").trim().toLowerCase()
        )
      );

      const supporterEmails = mailboxes
        .filter(
          (mailbox) =>
            String(mailbox?.role || "").trim().toLowerCase() === "supporter"
        )
        .map((mailbox) => mailbox?.email)
        .filter(Boolean);

      // const additionalEmails = (linkData?.additionalEmails || [])
      //   .map((email) => String(email || "").trim())
      //   .filter(Boolean);

      // Keep Reply All's current Cc result, then add supporters and
      // backend-provided additional emails.
      ccRecipients = uniqueAddressList([
        ...ccRecipients,
        ...supporterEmails,
        // ...additionalEmails,
      ]).filter((address) => {
        const normalisedAddress = String(address || "")
          .trim()
          .toLowerCase();

        return (
          normalisedAddress &&
          normalisedAddress !== selectedMailbox &&
          normalisedAddress !== currentUserEmail &&
          !toRecipientSet.has(normalisedAddress)
        );
      });
    }

    // Sent email from another user's mailbox:
    // keep the recipients generated above, then add that mailbox owner to Cc.
    // For the current user's own mailbox, nothing is added.
    if (
      isSentMessage &&
      !mailboxBelongsToCurrentUser &&
      selectedMailbox
    ) {
      const toRecipientSet = new Set(
        toRecipients.map((address) =>
          String(address || "").trim().toLowerCase()
        )
      );

      if (!toRecipientSet.has(selectedMailbox)) {
        ccRecipients = uniqueAddressList([
          ...ccRecipients,
          selectedMailbox,
        ]);
      }
    }

    setComposer({
      ...EMPTY_COMPOSER,
      // Keep ProjectEmailComposer in normal Reply mode for Reply with link.
      // isReplyWithLink tells sendComposer which template/button to inject.
      mode: isReplyWithLink ? "reply" : mode,
      isReplyWithLink,
      linkTemplate: isReplyWithLink ? linkTemplate : null,
      mailboxEmail: selectedMessage.mailboxEmail,
      messageID: selectedMessage.id,
      originalMessage: selectedMessage,
      subject:
        mode === "forward"
          ? forwardSubject(selectedMessage.subject)
          : replySubject(selectedMessage.subject),
      toRecipients: mode === "forward" ? [] : toRecipients,
      ccRecipients: mode === "forward" ? [] : ccRecipients,
      bccRecipients: [],
      body: isReplyWithLink ? linkTemplate?.value || "" : "",
    });
  };

  const sendComposer = async (composerPayload) => {
    if (!composer) return;

    setSending(true);

    const selectedLinkTemplate = composer.isReplyWithLink
      ? composer.linkTemplate
      : null;

    const bodyWithLink = selectedLinkTemplate?.linkedButton
      ? composerPayload.body.replace(
          "{{BUTTON_DO_NOT_REMOVE}}",
          selectedLinkTemplate.linkedButton
        )
      : composerPayload.body;

    try {
      const commonPayload = {
        projectID,
        expertID,
        accessToken: getCurrentUserGraphToken(),
        mailboxEmail: composerPayload.mailboxEmail,
        messageID: composer.messageID,
        subject: composerPayload.subject,
        body: bodyWithLink,
        toRecipients: composerPayload.toRecipients,
        ccRecipients: composerPayload.ccRecipients,
        bccRecipients: composerPayload.bccRecipients,
        attachments: composerPayload.attachments || [],
      };

      if (composer.mode === "compose") {
        await postJSON("send-project-client-email", commonPayload);
      } else if (composer.mode === "reply") {
        // Reply with link intentionally uses the same Graph reply flow.
        await postJSON("reply-project-client-email", commonPayload);
        if (logFunction && typeof logFunction === "function") {
          logFunction({
            sendTo: composerPayload.toRecipients,
            ccMail: composerPayload.ccRecipients,
          });
        }
      } else if (composer.mode === "replyAll") {
        await postJSON("reply-all-project-client-email", commonPayload);
        if (logFunction && typeof logFunction === "function") {
          logFunction({
            sendTo: composerPayload.toRecipients,
            ccMail: composerPayload.ccRecipients,
          });
        }
      } else {
        await postJSON("forward-project-client-email", commonPayload);
      }

      setComposer(null);

      Swal.fire({
        icon: "success",
        title: "Email sent",
        timer: 1300,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      await loadEmails();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not send email",
        text: error.message,
      });
    } finally {
      setSending(false);
    }
  };

  // Api to get client info
  const getClientInfo = () => {
    const url = `${API_URL}get-client-details-from-zoho`;
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ projectID }),
    })
      .then((res) => res.json())
      .then((data) => {
        setClientName(data.clientDetails);
      })
      .catch((err) => {
        console.log("error while getting client info");
      });
  };

  // API to fetch client link data
  const getReplyWithLinkData = async () => {
    try {
      const response = await fetch(
        `${API_URL}get-reply-with-link-data`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            projectID
          }),
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setLinkData(data.linkData);
      }
    } catch (error) {
      console.error("Error while getting client link:", error);
    }
  };

  useEffect (() => {
    if (isLinkCreated) {
      getClientInfo();
      getReplyWithLinkData();
    }
  }, [isLinkCreated]);

  // Prepare default email templates based on project type
  useEffect(() => {
    if (!linkData.link) return;
    if (linkData.projectType === "exp") {
      const templates = generateExpTemplates(
        clientName.lastName,
        userData.fullname,
        projectName,
        linkData.sharedProfileSummery
      );
      setDefaultTemplate(templates);
    } else {
      const templates = generateConTemplates(
        clientName.lastName,
        userData.fullname,
        projectName
      );
      setDefaultTemplate(templates);
    }
  }, [linkData.link, clientName]);

  // generating default templates for email with link
  const generateExpTemplates = (
    clientName,
    userName,
    projectName,
    sharedProfileSummary
  ) => [
    {
      lang: "en",
      label: "English",
      name: "Share Profile - English",
      subject: `expertpowerhouse | ${projectName}`,
      value: `
        <div>Dear ${getInformalName(clientName)},</div>
        <div><br></div>
        <div>Many thanks again for your request. You will find expert profiles following the below link.</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Below is the list of expert profiles with a short summary for a brief overview:</div>
        <div><br></div>
        ${sharedProfileSummary}
        <div>What do you think? Your feedback will be highly appreciated. If you want you can also select your preferences online and give availabilities for a call.</div>
        <div><br></div>
        <div>Best regards,</div>
        <div>${getInformalName(userName)}</div>
      `,
      linkedButton: `
        <table width="100%" cellspacing="0" cellpadding="0" role="presentation" style="margin: 20px 0;">
          <tr>
            <td align="center">
              <!--[if mso]>
              <a href="${linkData.link}" style="text-decoration:none;">
                <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml"
                            xmlns:w="urn:schemas-microsoft-com:office:word"
                            href="${linkData.link}"
                            style="height:40px;v-text-anchor:middle;width:220px;" arcsize="10%"
                            stroke="f" fillcolor="#365561">
                  <w:anchorlock/>
                  <center>
                    <span style="color:#ffffff; font-family:Arial,sans-serif; font-size:14px; font-weight:bold;">
                      Profile anzeigen
                    </span>
                  </center>
                </v:roundrect>
              </a>
              <![endif]-->

              <!--[if !mso]><!-- -->
              <table cellspacing="0" cellpadding="0" role="presentation">
                <tr>
                  <td align="center" bgcolor="#365561" style="border-radius: 6px;">
                    <a href="${linkData.link}"
                      target="_blank"
                      style="
                        font-size: 14px;
                        font-family: Arial, sans-serif;
                        color: #ffffff;
                        text-decoration: none;
                        padding: 12px 24px;
                        display: inline-block;
                        font-weight: bold;
                      ">
                      View Profiles
                    </a>
                  </td>
                </tr>
              </table>
              <!--<![endif]-->
            </td>
          </tr>
        </table>
      `,
    },
    {
      lang: "de",
      label: "German",
      name: "Share Profile - German (Formal)",
      subject: `expertpowerhouse | ${projectName}`,
      value: `
        <div>Hallo Herr/Frau ${getFormalName(clientName)},</div>
        <div><br></div>
        <div>vielen Dank nochmals für Ihre Anfrage. Die Expertenprofile finden Sie unter folgendem Link:</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Nachfolgend erhalten Sie die Liste der Profile mit einer kurzen Zusammenfassung für einen schnellen Überblick:</div>
        <div><br></div>
        ${sharedProfileSummary}
        <div><br></div>
        <div>Über Ihr Feedback freuen wir uns sehr. Gerne organisieren wir im nächsten Schritt Expertengespräche mit den passenden Kandidaten.</div>
        <div><br></div>
        <div>Viele Grüße</div>
        <div>${userName}</div>
      `,
      linkedButton: `
        <table width="100%" cellspacing="0" cellpadding="0" role="presentation" style="margin: 20px 0;">
          <tr>
            <td align="center">
              <!--[if mso]>
              <a href="${linkData.link}" style="text-decoration:none;">
                <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml"
                            xmlns:w="urn:schemas-microsoft-com:office:word"
                            href="${linkData.link}"
                            style="height:40px;v-text-anchor:middle;width:280px;" arcsize="10%"
                            stroke="f" fillcolor="#365561">
                  <w:anchorlock/>
                  <center>
                    <span style="color:#ffffff; font-family:Arial,sans-serif; font-size:14px; font-weight:bold;">
                      Profile anzeigen
                    </span>
                  </center>
                </v:roundrect>
              </a>
              <![endif]-->

              <!--[if !mso]><!-- -->
              <table cellspacing="0" cellpadding="0" role="presentation">
                <tr>
                  <td align="center" bgcolor="#365561" style="border-radius: 6px;">
                    <a href="${linkData.link}"
                      target="_blank"
                      style="
                        font-size: 14px;
                        font-family: Arial, sans-serif;
                        color: #ffffff;
                        text-decoration: none;
                        padding: 12px 24px;
                        display: inline-block;
                        font-weight: bold;
                      ">
                      Profile anzeigen
                    </a>
                  </td>
                </tr>
              </table>
              <!--<![endif]-->
            </td>
          </tr>
        </table>
      `,
    },
    {
      lang: "de",
      label: "German",
      name: "Share Profile - German (Informal)",
      subject: `expertpowerhouse | ${projectName}`,
      value: `
        <div>Hallo ${getInformalName(clientName)},</div>
        <div><br></div>
        <div>vielen Dank nochmals für deine Anfrage. Die Expertenprofile findest du unter folgendem Link:</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Nachfolgend findest du die Liste der Profile mit einer kurzen Zusammenfassung für einen schnellen Überblick:</div>
        <div><br></div>
        ${sharedProfileSummary}
        <div><br></div>
        <div>Wir freuen uns sehr über dein Feedback. Gerne organisieren wir im nächsten Schritt Gespräche mit den passenden Kandidaten.</div>
        <div><br></div>
        <div>Viele Grüße</div>
        <div>${getInformalName(userName)}</div>
      `,
      linkedButton: `
        <table width="100%" cellspacing="0" cellpadding="0" role="presentation" style="margin: 20px 0;">
          <tr>
            <td align="center">
              <!--[if mso]>
              <a href="${linkData.link}" style="text-decoration:none;">
                <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml"
                            xmlns:w="urn:schemas-microsoft-com:office:word"
                            href="${linkData.link}"
                            style="height:40px;v-text-anchor:middle;width:280px;" arcsize="10%"
                            stroke="f" fillcolor="#365561">
                  <w:anchorlock/>
                  <center>
                    <span style="color:#ffffff; font-family:Arial,sans-serif; font-size:14px; font-weight:bold;">
                      Profile anzeigen
                    </span>
                  </center>
                </v:roundrect>
              </a>
              <![endif]-->

              <!--[if !mso]><!-- -->
              <table cellspacing="0" cellpadding="0" role="presentation">
                <tr>
                  <td align="center" bgcolor="#365561" style="border-radius: 6px;">
                    <a href="${linkData.link}"
                      target="_blank"
                      style="
                        font-size: 14px;
                        font-family: Arial, sans-serif;
                        color: #ffffff;
                        text-decoration: none;
                        padding: 12px 24px;
                        display: inline-block;
                        font-weight: bold;
                      ">
                      Profile anzeigen
                    </a>
                  </td>
                </tr>
              </table>
              <!--<![endif]-->
            </td>
          </tr>
        </table>
      `,
    },
  ];

  const generateConTemplates = (clientName, userName, projectName) => [
    {
      lang: "en",
      label: "English",
      name: "Share Profile - English",
      subject: `expertpowerhouse | ${projectName}`,
      value: `
        <div>Dear ${getInformalName(clientName)},</div>
        <div><br></div>
        <div>Many thanks again for your request. You will find profiles following the below link.</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Your feedback will be highly appreciated and we will be very happy to set up get-to-know discussions with relevant candidates.</div>
        <div><br></div>
        <div>Best regards,</div>
        <div>${getInformalName(userName)}</div>
      `,
      linkedButton: `
        <table width="100%" cellspacing="0" cellpadding="0" role="presentation" style="margin: 20px 0;">
          <tr>
            <td align="center">
              <!--[if mso]>
              <a href="${linkData.link}" style="text-decoration:none;">
                <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml"
                            xmlns:w="urn:schemas-microsoft-com:office:word"
                            href="${linkData.link}"
                            style="height:40px;v-text-anchor:middle;width:220px;" arcsize="10%"
                            stroke="f" fillcolor="#365561">
                  <w:anchorlock/>
                  <center>
                    <span style="color:#ffffff; font-family:Arial,sans-serif; font-size:14px; font-weight:bold;">
                      View Profiles
                    </span>
                  </center>
                </v:roundrect>
              </a>
              <![endif]-->

              <!--[if !mso]><!-- -->
              <table cellspacing="0" cellpadding="0" role="presentation">
                <tr>
                  <td align="center" bgcolor="#365561" style="border-radius: 6px;">
                    <a href="${linkData.link}"
                      target="_blank"
                      style="
                        font-size: 14px;
                        font-family: Arial, sans-serif;
                        color: #ffffff;
                        text-decoration: none;
                        padding: 12px 24px;
                        display: inline-block;
                        font-weight: bold;
                      ">
                      View Profiles
                    </a>
                  </td>
                </tr>
              </table>
              <!--<![endif]-->
            </td>
          </tr>
        </table>
      `,
    },
    {
      lang: "de",
      label: "German",
      name: "Share Profile - German (Formal)",
      subject: `expertpowerhouse | ${projectName}`,
      value: `
        <div>Hallo Herr/Frau ${getFormalName(clientName)},</div>
        <div><br></div>
        <div>vielen Dank nochmals für Ihre Anfrage. Die Profile finden Sie unter folgendem Link:</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Über Ihr Feedback freuen wir uns sehr. Gerne organisieren wir im nächsten Schritt Kennenlern-Gespräche mit passenden Kandidaten.</div>
        <div><br></div>
        <div>Viele Grüße</div>
        <div>${userName}</div>
      `,
      linkedButton: `
        <table width="100%" cellspacing="0" cellpadding="0" role="presentation" style="margin: 20px 0;">
          <tr>
            <td align="center">
              <!--[if mso]>
              <a href="${linkData.link}" style="text-decoration:none;">
                <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml"
                            xmlns:w="urn:schemas-microsoft-com:office:word"
                            href="${linkData.link}"
                            style="height:40px;v-text-anchor:middle;width:280px;" arcsize="10%"
                            stroke="f" fillcolor="#365561">
                  <w:anchorlock/>
                  <center>
                    <span style="color:#ffffff; font-family:Arial,sans-serif; font-size:14px; font-weight:bold;">
                      Profile anzeigen
                    </span>
                  </center>
                </v:roundrect>
              </a>
              <![endif]-->

              <!--[if !mso]><!-- -->
              <table cellspacing="0" cellpadding="0" role="presentation">
                <tr>
                  <td align="center" bgcolor="#365561" style="border-radius: 6px;">
                    <a href="${linkData.link}"
                      target="_blank"
                      style="
                        font-size: 14px;
                        font-family: Arial, sans-serif;
                        color: #ffffff;
                        text-decoration: none;
                        padding: 12px 24px;
                        display: inline-block;
                        font-weight: bold;
                      ">
                      Profile anzeigen
                    </a>
                  </td>
                </tr>
              </table>
              <!--<![endif]-->
            </td>
          </tr>
        </table>
      `,
    },
    {
      lang: "de",
      label: "German",
      name: "Share Profile - German (Informal)",
      subject: `expertpowerhouse | ${projectName}`,
      value: `
        <div>Hallo ${getInformalName(clientName)},</div>
        <div><br></div>
        <div>vielen Dank nochmals für deine Anfrage. Die Profile findest du unter folgendem Link:</div>
        {{BUTTON_DO_NOT_REMOVE}}
        <div><br></div>
        <div>Wir freuen uns sehr über dein Feedback. Gerne organisieren wir im nächsten Schritt Kennenlern-Gespräche mit passenden Kandidaten.</div>
        <div><br></div>
        <div>Viele Grüße</div>
        <div>${getInformalName(userName)}</div>
      `,
      linkedButton: `
        <table width="100%" cellspacing="0" cellpadding="0" role="presentation" style="margin: 20px 0;">
          <tr>
            <td align="center">
              <!--[if mso]>
              <a href="${linkData.link}" style="text-decoration:none;">
                <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml"
                            xmlns:w="urn:schemas-microsoft-com:office:word"
                            href="${linkData.link}"
                            style="height:40px;v-text-anchor:middle;width:280px;" arcsize="10%"
                            stroke="f" fillcolor="#365561">
                  <w:anchorlock/>
                  <center>
                    <span style="color:#ffffff; font-family:Arial,sans-serif; font-size:14px; font-weight:bold;">
                      Profile anzeigen
                    </span>
                  </center>
                </v:roundrect>
              </a>
              <![endif]-->

              <!--[if !mso]><!-- -->
              <table cellspacing="0" cellpadding="0" role="presentation">
                <tr>
                  <td align="center" bgcolor="#365561" style="border-radius: 6px;">
                    <a href="${linkData.link}"
                      target="_blank"
                      style="
                        font-size: 14px;
                        font-family: Arial, sans-serif;
                        color: #ffffff;
                        text-decoration: none;
                        padding: 12px 24px;
                        display: inline-block;
                        font-weight: bold;
                      ">
                      Profile anzeigen
                    </a>
                  </td>
                </tr>
              </table>
              <!--<![endif]-->
            </td>
          </tr>
        </table>
      `,
    },
  ];

  return (
    <AnimatePresence>
      <motion.div
        className="project-client-email-root fixed inset-x-0 bottom-0 top-[78px] z-[80] flex items-center justify-center px-2 py-3 sm:px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/45 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ y: 18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 18, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="relative flex h-[calc(95vh-78px)] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.18),transparent_35%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_24px_80px_rgba(15,23,42,0.30)]"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="shrink-0 border-b border-slate-200/80 px-4 py-4 sm:px-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#365561]">
                  Project client mailbox
                </p>
                <h2 className="mt-1 truncate text-xl font-semibold text-slate-900 sm:text-2xl">
                  {projectName || `Project ${projectID}`}
                </h2>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="group relative inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-red-500 focus:outline-none"
              >
                <i className="fa-solid fa-xmark text-base" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
							<div className="flex h-full min-h-0 overflow-hidden md:grid md:grid-cols-[70px_minmax(0,1fr)] xl:flex">
								<aside
                  className="hidden h-full min-h-0 overflow-visible md:flex md:flex-col xl:hidden bg-blur"
                >
									<div className="grid shrink-0 gap-2">
										<div className="flex items-center justify-center border-b border-gray-200 mt-2 mb-2 py-2 ">
											<EmailIconButton
												icon="fa-envelope"
												label="Compose new email"
												onClick={openCompose}
												primary
											/>
										</div>
										{FOLDER_OPTIONS.map((folder) => (
											<NavButton
												key={folder.value}
												active={activeFolder === folder.value}
												icon={folder.icon}
												label={folder.fullLabel}
												count={counts[folder.value]}
												unread={folder.value === "inbox" && counts.unread > 0}
												compact
												onClick={() => changeFolder(folder.value)}
											/>
										))}
									</div>

									<div className="mt-auto hidden min-h-0 flex-col items-center gap-2 overflow-visible pb-3 md:flex xl:mt-auto xl:flex-row xl:flex-wrap xl:justify-start">
										{mailboxes.map((mailbox) => (
											<Avatar
												key={mailbox.email}
												name={mailbox.name}
												email={mailbox.email}
												tooltip={`${mailbox.name} · ${mailbox.role}`}
												sizeClass="h-9 w-9"
												textClass="text-[10px]"
                        callingFrom="supporter"
											/>
										))}
									</div>

								</aside>

								<div className="min-h-0 flex-1 overflow-hidden px-3 pb-3 pt-3 sm:px-4 lg:px-5 lg:pb-4">
									<div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden md:grid md:grid-cols-[250px_minmax(0,1fr)] md:gap-4 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[250px_350px_minmax(0,1fr)]">
										<aside
                    className="flex shrink-0 items-center justify-between rounded-3xl border-slate-200/80 md:hidden md:min-h-0 md:flex-col md:items-stretch md:border md:bg-white md:shadow-[0_10px_30px_rgba(15,23,42,0.06)] xl:flex xl:h-full xl:min-h-0 xl:p-4"
                  >
											<div className="min-w-0 flex-1 md:w-full">
												<div className="md:hidden">
													<FolderToggle
														value={activeFolder}
														onChange={changeFolder}
														counts={counts}
													/>
												</div>

												<div className="hidden lg:hidden xl:block">
													<div className="flex items-center justify-between gap-2">
														<p className="text-base font-semibold text-slate-900">Mail folders</p>

														<div className="flex items-center gap-2">
															<TooltipIconButton
																icon="fa-plus"
																label="Compose new email"
																onClick={openCompose}
																primary
															/>
															<TooltipIconButton
																icon="fa-rotate"
																label="Refresh emails"
																onClick={() => loadEmails()}
																disabled={loading}
															/>
														</div>
													</div>

													<div className="mt-4 grid gap-2">
														{FOLDER_OPTIONS.map((folder) => (
															<NavButton
																key={folder.value}
																active={activeFolder === folder.value}
																icon={folder.icon}
																label={folder.fullLabel}
																count={counts[folder.value]}
																unread={folder.value === "inbox" && counts.unread > 0}
																onClick={() => changeFolder(folder.value)}
															/>
														))}
													</div>
												</div>
											</div>

											<div className="ml-2 flex shrink-0 items-center gap-2 md:ml-0 md:mt-auto  md:hidden xl:hidden">
												<TooltipIconButton
													icon="fa-plus"
													label="Compose new email"
													onClick={openCompose}
													primary
												/>
												<TooltipIconButton
													icon="fa-rotate"
													label="Refresh emails"
													onClick={() => loadEmails()}
													disabled={loading}
													isRightEdge
												/>
											</div>

											<div className="hidden md:mt-4 md:flex md:flex-col md:items-center md:gap-2 xl:mt-auto xl:flex-row xl:flex-wrap xl:justify-start xl:pt-4">
												{mailboxes.map((mailbox) => (
													<Avatar
														key={mailbox.email}
														name={mailbox.name}
														email={mailbox.email}
														tooltip={`${mailbox.name} · ${mailbox.role}`}
														sizeClass="h-9 w-9"
														textClass="text-[10px]"
                            callingFrom="supporter"
													/>
												))}
											</div>
										</aside>

										<aside
											className={[
												"h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)] md:flex",
												showResponsiveDetail ? "hidden xl:flex" : "flex",
											].join(" ")}
										>
											<div className="mb-3 flex items-center justify-between gap-2">
												<div>
													<p className="text-base font-semibold text-slate-900">
														{activeFolder === "all"
															? "All messages"
															: activeFolder === "inbox"
																? "Inbox"
																: "Sent"}
													</p>
													<p className="text-xs text-slate-500">
														{visibleMessages.length} message
														{visibleMessages.length === 1 ? "" : "s"}
													</p>
												</div>

												{loading ? (
													<i className="fa-solid fa-spinner fa-spin text-[#365561]" />
												) : (
													<div className="hidden md:block xl:hidden">
														<TooltipIconButton
															icon="fa-rotate"
															label="Refresh emails"
															onClick={() => loadEmails()}
															disabled={loading}
															isRightEdge
														/>
													</div>
												)}
											</div>

											<div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1">
												{!loading && !visibleMessages.length ? (
													<div className="flex min-h-[260px] items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-5 text-center">
														<div>
															<i className="fa-regular fa-envelope text-2xl text-slate-300" />
															<p className="mt-2 text-sm font-semibold text-slate-600">
																No client emails found
															</p>
															<p className="mt-1 text-xs text-slate-400">
																Nothing matched this project date range and client address.
															</p>
														</div>
													</div>
												) : (
													visibleMessages.map((message) => (
														<MessageListItem
															key={`${message.mailboxEmail}-${message.folder}-${message.id}`}
															message={message}
															selected={selectedMessageID === message.id}
															onClick={() => selectMessage(message)}
														/>
													))
												)}
											</div>
										</aside>

										<section
											className={[
												"h-full min-h-0 min-w-0 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)] md:block",
												showResponsiveDetail ? "block" : "hidden xl:block",
											].join(" ")}
										>
                      <AnimatePresence mode="wait" initial={false}>
                        {composer ? (
                          <motion.div
                            key={`composer-${composer.mode}`}
                            initial={{ x: 90, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 90, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 340, damping: 32 }}
                            className="h-full min-h-0"
                          >
                            <ProjectEmailComposer
                              mode={composer.mode}
                              mailboxes={mailboxes}
                              initialMailboxEmail={composer.mailboxEmail}
                              initialTo={composer.toRecipients || []}
                              initialCc={composer.ccRecipients || []}
                              initialBcc={composer.bccRecipients || []}
                              initialSubject={composer.subject || ""}
                              initialBody={composer.body || ""}
                              originalMessage={composer.originalMessage || null}
                              projectName={projectName}
                              projectID={projectID}
                              sending={sending}
                              onCancel={() => setComposer(null)}
                              onSend={sendComposer}
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="message-detail"
                            initial={{ x: -35, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: -35, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="h-full min-h-0"
                          >
                            <MessageDetail
                              isLink={Boolean(linkData.link)}
                              message={selectedMessage}
                              onReply={() => openResponse("reply")}
                              onReplyAll={() => openResponse("replyAll")}
                              onForward={() => openResponse("forward")}
                              onReplyWithLink={(template) => openResponse("link", template)}
                              replyWithLinkTemplates={defaultTemplate}
                              canRespond={canRespondToSelectedMessage}
                              onBack={closeResponsiveDetail}
                              showBackButton={showResponsiveDetail}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
										</section>
									</div>
							
								</div>
							</div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
